import { QuaggaProcessResult } from 'services/barcode-decoder.service';
import {
  EMA_ALPHA,
  MISSING_FRAME_GRACE,
} from './barcode-tracker-constants';

export interface TrackerRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface TrackerPick {
  value: string;
  type: string;
  /** EMA-smoothed bounding rect in decoder (Quagga) source space. */
  rect: TrackerRect;
}

interface Candidate {
  rect: TrackerRect;
  area: number;
}

/** Computes the axis-aligned bounding rect of a 4-point polygon
 *  (`number[][]` shaped as `[[x,y], [x,y], [x,y], [x,y]]`). Returns null
 *  when the polygon is missing or degenerate (no points). */
function polyToRect(poly: number[][] | undefined | null): TrackerRect | null {
  if (!poly || poly.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const pt of poly) {
    if (!pt || pt.length < 2) continue;
    const x = pt[0];
    const y = pt[1];
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null;
  return { x: minX, y: minY, w: Math.max(0, maxX - minX), h: Math.max(0, maxY - minY) };
}

function rectArea(r: TrackerRect): number {
  return Math.max(0, r.w) * Math.max(0, r.h);
}

function blendRect(prev: TrackerRect, next: TrackerRect, alpha: number): TrackerRect {
  const inv = 1 - alpha;
  return {
    x: prev.x * inv + next.x * alpha,
    y: prev.y * inv + next.y * alpha,
    w: prev.w * inv + next.w * alpha,
    h: prev.h * inv + next.h * alpha,
  };
}

/**
 * Per-frame selection + hysteresis layer that turns Quagga's noisy
 * multi-candidate `result.boxes` into a single tracked rect.
 *
 * Behavior:
 *
 *   - **No prior lock**: pick the largest-area candidate this frame.
 *     Lock onto its decoded value if the frame has a confirmed
 *     `codeResult.code`.
 *   - **Tracked value present this frame**: stick on the tracked value
 *     and bias the visible rect toward the largest candidate.
 *   - **Tracked value absent this frame** (or all candidates absent):
 *     tick `missingFrameCount`. On reaching `MISSING_FRAME_GRACE` (2),
 *     release the lock — `trackedValue`, `missingFrameCount`, and
 *     `smoothedRect` all reset together so the next pick blends from
 *     scratch.
 *
 * After selection, the chosen rect is EMA-smoothed (α = `EMA_ALPHA`,
 * 0.35) against the previous frame's smoothed rect to damp Quagga's
 * per-frame jitter.
 *
 * **Hysteresis vs iOS native**: iOS native uses a 1.5× takeover ratio
 * (`TAKEOVER_AREA_RATIO` in barcode-tracker-constants) to swap to a
 * dwarfingly-larger same-code candidate within a frame. Quagga reports
 * one confirmed value per frame across all candidates — so even on a
 * "takeover" we cannot tell that a different candidate carries a
 * different code, and `winner = largest` already covers the "swap to
 * the biggest" intent. The takeover comparison is therefore omitted
 * here; the 2-frame `MISSING_FRAME_GRACE` is the entire hysteresis on
 * web. The constant is retained in barcode-tracker-constants for
 * cross-platform consistency.
 *
 * **Partial frames**: Quagga reports `result.boxes` with
 * `codeResult.code` undefined when it sees a barcode-shaped region but
 * hasn't decoded a value. These treat the tracked value as ABSENT,
 * ticking the grace counter. The 400ms hold timer in `cs-camera-view`
 * compensates visually; the brackets continue to track the largest
 * candidate for up to 2 partial frames before the lock releases.
 * A future migration to the browser's native `BarcodeDetector` API
 * (Chromium-only) would expose per-candidate values and let us treat
 * "decoded box present" as "tracked still alive" cleanly.
 */
export class BarcodeTrackerSelector {
  private trackedValue: string | undefined;
  private missingFrameCount = 0;
  private smoothedRect: TrackerRect | undefined;

  /** Drop all state. Call when the host component tears down detection
   *  or when the strategy switches mid-session. Same shape as the
   *  internal release path — both clear every piece of derived state
   *  so the next pick starts fresh. */
  reset(): void {
    this.releaseLock();
  }

  /** Run selection + hysteresis + EMA against one Quagga `onProcessed`
   *  result. Returns the picked candidate (with smoothed rect) or null
   *  when no usable candidate is available this frame. */
  selectCandidate(result: QuaggaProcessResult): TrackerPick | null {
    const code = result?.codeResult?.code;
    const format = result?.codeResult?.format ?? 'unknown';
    const boxes = result?.boxes ?? [];

    const candidates: Candidate[] = [];
    for (const poly of boxes) {
      const rect = polyToRect(poly);
      if (!rect) continue;
      const area = rectArea(rect);
      if (area <= 0) continue;
      candidates.push({ rect, area });
    }

    if (candidates.length === 0) {
      // Empty frame symmetry — same grace counter as "tracked value
      // absent among candidates" so a prolonged dropout still releases
      // the lock instead of holding stale state forever.
      if (this.trackedValue !== undefined) {
        this.missingFrameCount += 1;
        if (this.missingFrameCount >= MISSING_FRAME_GRACE) {
          this.releaseLock();
        }
      }
      return null;
    }

    // largest-area candidate this frame
    let largest = candidates[0];
    for (let i = 1; i < candidates.length; i++) {
      if (candidates[i].area > largest.area) largest = candidates[i];
    }

    // Quagga only confirms one code per frame; treat the frame's
    // codeResult.code as "the tracked value is present in this frame"
    // when it equals our trackedValue. Partial frames (boxes present
    // but no codeResult.code) count as ABSENT — grace counter ticks
    // and the hold timer in cs-camera-view absorbs the visual gap.
    const trackedPresent = !!code && code === this.trackedValue;

    let winner: Candidate;
    let winnerValue: string;

    if (this.trackedValue !== undefined && trackedPresent) {
      // Lock is alive. Stick on the tracked value and bias the visible
      // rect toward the largest candidate. The takeover-ratio
      // comparison would normally swap to a much-larger same-code
      // candidate, but Quagga reports a single `codeResult.code` per
      // frame across all candidates — so even on takeover we'd keep
      // the same value AND the same `winner = largest`. With nothing
      // observable to differentiate, the conditional collapses; the
      // takeover ratio constant is retained in the codebase for
      // cross-platform parity (iOS native uses it against per-box
      // values).
      winner = largest;
      winnerValue = this.trackedValue;
      this.missingFrameCount = 0;
    } else if (this.trackedValue !== undefined) {
      // Had a lock; tracked value isn't present this frame. Tick grace.
      this.missingFrameCount += 1;
      if (this.missingFrameCount < MISSING_FRAME_GRACE) {
        return null;
      }
      // Release + re-pick this frame.
      this.releaseLock();
      winner = largest;
      winnerValue = code ?? '';
      if (code) {
        this.trackedValue = code;
      }
    } else {
      // No prior lock. If the frame has a confirmed code, lock onto it;
      // otherwise still emit the largest candidate's rect so the brackets
      // can morph onto a partial detection (matches the visual feedback
      // the page already shows for `partial` status).
      winner = largest;
      winnerValue = code ?? '';
      if (code) {
        this.trackedValue = code;
      }
      this.missingFrameCount = 0;
    }

    // EMA smoothing against the previous smoothed rect (or fresh start
    // after a release — `smoothedRect` was cleared together with the
    // lock so we won't blend a stale rect against a new candidate).
    const smoothed = this.smoothedRect
      ? blendRect(this.smoothedRect, winner.rect, EMA_ALPHA)
      : winner.rect;
    this.smoothedRect = smoothed;

    return {
      value: winnerValue,
      type: format,
      rect: smoothed,
    };
  }

  /** Drop the lock plus all derived state. Called from both release
   *  paths (empty-frame grace exhausted, tracked-absent grace
   *  exhausted) so the next pick blends from a fresh baseline. */
  private releaseLock(): void {
    this.trackedValue = undefined;
    this.missingFrameCount = 0;
    this.smoothedRect = undefined;
  }
}
